import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { login } from "../../actions/authActions";

import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import {
  Container,
  Form,
  Button,
  Row,
  Col,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import "./login.css";

const DEFAULT_LOGIN_ERROR =
  "Unable to log in. Please check your details and try again.";

const isHtmlError = (value) => {
  const text = String(value || "").trim().toLowerCase();

  return (
    text.startsWith("<!doctype html") ||
    text.startsWith("<html") ||
    text.includes("<body") ||
    text.includes("<title>403 forbidden</title>") ||
    text.includes("csrf verification failed") ||
    text.includes("origin checking failed")
  );
};

const cleanLoginError = (value) => {
  const message = String(value || "").trim();

  if (!message || message === "Login Success") {
    return "";
  }

  if (isHtmlError(message)) {
    return "Login request was blocked by the server. Please refresh the page and try again.";
  }

  return message;
};

const Login = () => {
  const dispatch = useDispatch();
  const { msg: authMsg } = useSelector((s) => s.authReducer);
  const navigate = useNavigate();
  const location = useLocation();

  const [signupSuccess, setSignupSuccess] = useState(
    Boolean(location.state?.signupSuccess)
  );

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState("");
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [showErrorToast, setShowErrorToast] = useState(false);

  const { email, password } = formData;

  const displayError = useMemo(() => {
    return cleanLoginError(apiError || authMsg);
  }, [apiError, authMsg]);

  useEffect(() => {
    if (displayError) {
      setShowErrorToast(true);
    }
  }, [displayError]);

  const closeErrorToast = () => {
    setShowErrorToast(false);
    setApiError("");
  };

  const onChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setApiError("");
    setShowErrorToast(false);
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const next = { email: "", password: "" };

    if (!email.trim()) {
      next.email = "Email or username is required.";
    }

    if (!password.trim()) {
      next.password = "Password is required.";
    }

    setErrors(next);

    return !next.email && !next.password;
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!validate() || submitting) {
      return;
    }

    setSubmitting(true);
    setApiError("");
    setShowErrorToast(false);

    try {
      const result = await dispatch(login(email.trim(), password));

      if (result?.ok) {
        navigate("/", { replace: true });
        return;
      }

      setErrors((prev) => ({
        ...prev,
        ...(result?.fieldErrors || {}),
      }));

      setApiError(result?.error || DEFAULT_LOGIN_ERROR);
      setShowErrorToast(true);
    } catch (err) {
      setApiError("Unable to log in. Please try again.");
      setShowErrorToast(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navbar />

      <ToastContainer
        position="top-end"
        className="p-3"
        style={{
          position: "fixed",
          top: "1rem",
          right: "1rem",
          zIndex: 9999,
        }}
      >
        <Toast
          bg="success"
          show={signupSuccess}
          onClose={() => setSignupSuccess(false)}
          delay={4500}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Account created</strong>
          </Toast.Header>
          <Toast.Body className="text-white">
            Account created successfully. Please log in.
          </Toast.Body>
        </Toast>

        <Toast
          bg="danger"
          show={Boolean(displayError) && showErrorToast}
          onClose={closeErrorToast}
          delay={6000}
          autohide
        >
          <Toast.Header closeButton>
            <strong className="me-auto">Login error</strong>
          </Toast.Header>
          <Toast.Body className="text-white">{displayError}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Container className="form-container">
        <Row className="justify-content-md-center">
          <Col xs={11} md={8} className="form-column">
            <h2>Login</h2>

            <Form onSubmit={onSubmit} noValidate className="mt-3">
              <Form.Group controlId="formBasicEmail" className="mb-3">
                <Form.Label>Email or username</Form.Label>
                <Form.Control
                  type="text"
                  name="email"
                  placeholder="Enter email or username"
                  value={email}
                  onChange={onChange}
                  autoComplete="username"
                  isInvalid={Boolean(errors.email)}
                  disabled={submitting}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formBasicPassword" className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={onChange}
                  autoComplete="current-password"
                  isInvalid={Boolean(errors.password)}
                  disabled={submitting}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>

              <div className="d-flex align-items-center gap-2">
                <Button
                  type="submit"
                  className="login-button mt-2"
                  variant="primary"
                  disabled={submitting}
                  aria-busy={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Logging in...
                    </>
                  ) : (
                    "Login"
                  )}
                </Button>
              </div>
            </Form>

            <p className="mt-3">
              Do not have an account?{" "}
              <Link to="/signup">
                <span>Register here</span>
              </Link>
            </p>
          </Col>
        </Row>
      </Container>

      <Footer />
    </>
  );
};

export default Login;